// The body of the End Game dialog, shared by the control panel and the home screen's live games.
// From home it can also delete the game outright, leaving no history.

import { useState } from "react";
import { Button, Label, Textarea } from "../ui/primitives.js";

type Result = { winningTeam: "good" | "evil"; notes: string };

export function EndGameForm({
  onEnd,
  onDelete,
}: {
  /** Resolves true when the server accepted it. */
  onEnd: (result: Result) => Promise<boolean>;
  onDelete?: () => Promise<boolean>;
}) {
  const [winner, setWinner] = useState<"good" | "evil">("good");
  const [notes, setNotes] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  const run = async (action: () => Promise<boolean>): Promise<void> => {
    setBusy(true);
    await action();
    setBusy(false);
  };

  return (
    <div className="space-y-3">
      <div>
        <Label>Winning team</Label>
        <div className="mt-1 flex gap-2">
          <Button variant={winner === "good" ? "primary" : "secondary"} onClick={() => setWinner("good")}>
            Good
          </Button>
          <Button variant={winner === "evil" ? "primary" : "secondary"} onClick={() => setWinner("evil")}>
            Evil
          </Button>
        </div>
      </div>
      <div>
        <Label htmlFor="cc-notes">Notes (optional)</Label>
        <Textarea
          id="cc-notes"
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="How did it go?"
          className="mt-1"
        />
      </div>
      <Button
        variant="danger"
        className="w-full"
        disabled={busy}
        onClick={() => void run(() => onEnd({ winningTeam: winner, notes }))}
      >
        End the game
      </Button>

      {onDelete && (
        <div className="border-t border-ink-line pt-3">
          {confirmDelete ? (
            <div className="space-y-2">
              <p className="text-xs text-parchment-dim">
                The game disappears for everyone and nothing is added to anyone&apos;s history.
              </p>
              <div className="flex gap-2">
                <Button variant="danger" disabled={busy} onClick={() => void run(onDelete)}>
                  Really delete
                </Button>
                <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="ghost" className="w-full" onClick={() => setConfirmDelete(true)}>
              Delete without recording
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
